import type VectorLayer from 'ol/layer/Vector'
import type VectorSource from 'ol/source/Vector'

import { createNonBlockingRenderByTime } from '../../renderingQueue'
import { getLayerMetadata, setLayerMetadata } from '../layerMetadata'

import { findFeature } from '../findFeature'

import { updatePlaceFeature } from './utils/updatePlaceFeature'
import { findPlaceMarker } from './utils/findPlaceMarker'

const nonblockingRender = createNonBlockingRenderByTime()

export const updatePlacesMapSelection = async (
  layer: VectorLayer<VectorSource>,
  mapMarkers: uui.domain.ui.map.markers.MapMarkers['place'],
  mapStyles: uui.domain.ui.map.markers.MapStyles['place'],
  selection: Set<string>,
) => {
  // retrieve selected items in the current layer
  const layerSelection = (getLayerMetadata(layer, 'selection') ?? new Set()) as Set<string>

  // if prev and next are empty do nothing
  if (selection.size !== 0 || layerSelection.size !== 0) {
    const placesToDeselect = new Set(layerSelection)

    nonblockingRender.reset()

    for (const markerId of selection) {
      await nonblockingRender.next()

      // remove it from placeId to be deselected
      placesToDeselect.delete(markerId)

      if (layerSelection.has(markerId)) {
        // placeId was previously selected and it's still selected
        // do nothing
        continue
      }

      const marker = findPlaceMarker(mapMarkers, markerId)
      if (!marker) {
        throw new Error(
          `Trying to select Place marker with lat-lng: ${markerId} for ID: ${markerId} but no feature exist on the map`,
        )
      }

      const feature = findFeature(layer, marker.id)

      if (!feature) {
        throw new Error(
          `Trying to select Place marker with lat-lng: ${markerId} for ID: ${markerId} but no feature exist on the map`,
        )
      }

      // the placeId is a new selection
      updatePlaceFeature(mapStyles, marker, feature, true)
    }

    nonblockingRender.reset()

    for (const markerId of placesToDeselect) {
      await nonblockingRender.next()

      const marker = findPlaceMarker(mapMarkers, markerId)
      if (!marker) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `Trying to deselect Place marker with lat-lng: ${markerId} for ID: ${markerId} but no feature exist on the map`,
          )
        }
        continue
      }

      const feature = findFeature(layer, marker.id)

      if (!feature) {
        // fail silently in production when deselecting unknown items
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `Trying to deselect Place marker with lat-lng: ${markerId} for ID: ${markerId} but no feature exist on the map`,
          )
        }

        continue
      }

      // deselect the place
      updatePlaceFeature(mapStyles, marker, feature, false)
    }
  }

  // update the stored collection
  setLayerMetadata(layer, 'selection', selection)
}
